import "./App.css";
import "./EntryText.css";
import { useState, useRef, useEffect } from "react";
import arrow from "./photos/arrow.svg";

import { useTranslation } from "react-i18next";

import PaymentDrawer from "./Components/PaymentDrawer/PaymentDrawer";
import Recommendation from "./Components/Recommendation";
import WaitingForPromptsRender from "./Components/WaitingForPromptsRender";
import PromptCounter from "./Components/PromptCounter";
import AddEntry from "./Components/AddEntry";
import { useClassicRoomSocket } from "./hooks/useClassicRoomSocket";
import { entryRender } from "./Components/entryRender.tsx";
import { getBlockedSuggestions } from "./utils/blockedSuggestions";

const AI_SHOW_LIMIT = 10;
const AI_BEFORE_ROUND_SHOW_LIMIT = 15;

const DISABLE_AI = false; // Set to true to disable AI functionality

export function NewRoom(props) {
  const [roomInput, setRoomInput] = useState("");
  const [start, setStart] = useState(false);
  const [gameStarted, setGameStarted] = useState(false);
  const [inGameLength, setInGameLength] = useState(0);

  const [messages, setMessages] = useState([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [previousIndex, setPreviousIndex] = useState(null);

  const [isAnimating, setIsAnimating] = useState(false);
  // Re-entry lock for the "next" round-trip, kept separate from isAnimating so
  // gating rapid taps never triggers a slide animation. See next().
  const nextRequestInFlight = useRef(false);

  const [openRecommendation, setOpenRecommendation] = useState(false);

  const [waitingForPrompts, setWaitingForPrompts] = useState(false);

  const [isPaymentDrawerOpen, setIsPaymentDrawerOpen] = useState(false);
  const [showPaidPaymentDrawer, setShowPaidPaymentDrawer] = useState(false);
  const [translateYDrawer, setTranslateYDrawer] = useState(0);

  const [startOverOpen, setStartOverOpen] = useState(false);

  const textArea = useRef(null);
  const continueButtonRef = useRef(null);
  const [continueButtonHeight, setContinueButtonHeight] = useState(0);

  const [mobileNumber, setMobileNumber] = useState("");

  const { t } = useTranslation();

  const [numPrompts, setNumPrompts] = useState(0);

  useClassicRoomSocket(
    props.socket,
    props.roomName,
    setNumPrompts,
    setWaitingForPrompts,
    setGameStarted,
    setInGameLength,
  );

  const onChangeMobileInput = (e) => {
    const input = e.currentTarget.value;
    if (/^\d*$/.test(input)) {
      setMobileNumber(input);
    }
  };

  const setCountupValue = () => {
    fetch(props.fetchString + props.roomName, {
      headers: {
        Accept: "application/json",
      },
    })
      .then((res) => res.json())
      .then((data) => {
        if (data?.data?.prompts) {
          setNumPrompts(data.data.prompts.length);
          setGameStarted(!!data.data.gameStarted);
        }
      });
  };

  useEffect(() => {
    setCountupValue();
    const saved = localStorage.getItem("mobileNumber");
    if (saved) {
      setMobileNumber(saved);
    }
  }, []);

  // Keep the chevron button square by matching its width and height to the
  // primary continue button. Height varies with locale text wrapping, so we
  // observe the live element rather than fixing a value.
  useEffect(() => {
    const el = continueButtonRef.current;
    if (!el || typeof ResizeObserver === "undefined") return;
    // Cap the measured height: the chevron's width is derived from this value,
    // and a wider chevron steals width from the flex:1 continue button, wrapping
    // its text taller — a feedback loop that runs away without a ceiling.
    const MAX_CHEVRON_SIZE = 86;
    const update = () =>
      setContinueButtonHeight(Math.min(el.offsetHeight, MAX_CHEVRON_SIZE));
    update();
    const ro = new ResizeObserver(update);
    ro.observe(el);
    return () => ro.disconnect();
  }, [start, gameStarted]);

  // Collapse the inline start-over button whenever we leave or re-enter the
  // lobby so it doesn't sit open across navigation.
  useEffect(() => {
    if (start) setStartOverOpen(false);
  }, [start]);

  const blockedSuggestions = getBlockedSuggestions(t);

  const add = () => {
    if (roomInput !== "") {
      if (blockedSuggestions.includes(roomInput)) {
        props.showInfoMessage("warning", t("suggestion-incomplete"), false);
        return;
      }
      props.socket.emit("send classic prompt", [props.roomName, roomInput]);
      setRoomInput("");
      props.showInfoMessage("", t("added"), true, "short");
    }
    document.getElementById("page").scrollIntoView();
  };

  const BackButton = () => {
    // From inside the game view, peel back to the room's lobby so the user
    // doesn't accidentally drop all the way out of the room. The cached
    // messages array and currentIndex stay intact, so tapping CONTINUE GAME
    // again puts them right back where they were.
    if (start) {
      setStart(false);
      return;
    }
    // From the lobby, BACK leaves the room entirely.
    props.setRoomState("start screen");
    props.socket.emit("leave room", props.roomName);
  };

  // Cache a freshly-acked prompt at its server-reported index.
  const cachePrompt = (index, value) => {
    setMessages((prev) => {
      const next = prev.slice();
      next[index] = value;
      return next;
    });
  };

  // First-ever start in this room (path empty server-side).
  const firstStart = () => {
    props.socket.emit(
      "classic start game",
      { roomName: props.roomName },
      (result) => {
        if (!result || result.empty) {
          props.showInfoMessage("warning", t("nothing"), false);
          return;
        }
        cachePrompt(result.index, result.prompt.value);
        setCurrentIndex(result.index);
        setStart(true);
      },
    );
  };

  // Resume at the room watermark (highest index reached on any device).
  const handleContinue = () => {
    const target = Math.max(0, inGameLength - 1);
    props.socket.emit(
      "classic next prompt",
      { roomName: props.roomName, requestedIndex: target },
      (result) => {
        if (!result || result.empty) {
          // Falls through to a fresh first-start if the server has somehow
          // lost the path (e.g., room expired). Should be rare.
          firstStart();
          return;
        }
        cachePrompt(result.index, result.prompt.value);
        setCurrentIndex(result.index);
        setStart(true);
      },
    );
  };

  // Start the same shuffled deck over from index 0 on this device only.
  // Server's path is preserved so other devices' watermark is unaffected.
  const handleStartOver = () => {
    props.socket.emit(
      "classic start game",
      { roomName: props.roomName },
      (result) => {
        if (!result || result.empty) {
          props.showInfoMessage("warning", t("nothing"), false);
          return;
        }
        cachePrompt(result.index, result.prompt.value);
        setCurrentIndex(result.index);
        setStart(true);
      },
    );
  };

  // Lobby's primary button: tap to start (first time) or resume directly
  // at the room watermark (game already started).
  const handleStartButtonClick = () => {
    if (gameStarted) {
      handleContinue();
    } else {
      firstStart();
    }
  };

  // Chevron next to the primary button: reveal/hide the inline
  // "Start fra begynnelsen" secondary button below.
  const toggleStartOver = () => {
    setStartOverOpen((v) => !v);
  };

  const next = () => {
    if (isAnimating || nextRequestInFlight.current) return;
    const requestedIndex = currentIndex + 1;
    if (messages[requestedIndex] !== undefined) {
      animateTo(requestedIndex);
      return;
    }
    // Block re-entry while waiting on the server WITHOUT touching isAnimating:
    // isAnimating drives the slide-in/out classes, and flipping it true here —
    // before currentIndex advances — would animate the still-current prompt
    // (yank it off-screen and back) for the whole server round-trip. The ref
    // gates rapid taps without any visual side effect; the animation only
    // begins in animateTo once the next prompt is cached.
    nextRequestInFlight.current = true;
    props.socket.emit(
      "classic next prompt",
      { roomName: props.roomName, requestedIndex },
      (result) => {
        nextRequestInFlight.current = false;
        if (!result || result.empty) {
          props.showInfoMessage("warning", t("empty"), false);
          if (showAiButton) handleOpenDrawerClick();
          return;
        }
        cachePrompt(result.index, result.prompt.value);
        animateTo(result.index);
      },
    );
  };

  const animateTo = (targetIndex) => {
    setIsAnimating(true);
    setPreviousIndex(currentIndex);
    setCurrentIndex(targetIndex);
    setTimeout(() => {
      setPreviousIndex(null);
      setIsAnimating(false);
    }, 700);
  };

  const prev = () => {
    if (isAnimating) return;
    if (currentIndex <= 0) return;
    const target = currentIndex - 1;
    if (messages[target] !== undefined) {
      setCurrentIndex(target);
      return;
    }
    // Cache miss — happens after a "Continue where you left off" resume,
    // where only the watermark prompt is in the local cache.
    props.socket.emit(
      "classic next prompt",
      { roomName: props.roomName, requestedIndex: target },
      (result) => {
        if (!result || result.empty) return;
        cachePrompt(result.index, result.prompt.value);
        setCurrentIndex(target);
      },
    );
  };

  const showAiButtonBeforeRound =
    !DISABLE_AI && numPrompts > AI_BEFORE_ROUND_SHOW_LIMIT - 1 && !start;

  const showAiButton =
    !DISABLE_AI &&
    numPrompts > AI_SHOW_LIMIT - 1 &&
    numPrompts - currentIndex - 1 < AI_SHOW_LIMIT + 1;

  const divStyle = {
    top: props.containerHeight / 6 - (showAiButton ? 52 : 0),
  };

  // function entryRender() {
  //   return (
  //     <div className="slide-container" style={divStyle}>
  //       {previousIndex !== null && (
  //         <div
  //           className={`${getTextClass(
  //             messages[previousIndex].length
  //           )} slide-text ${isAnimating ? "slide-out" : ""}`}
  //           onAnimationEnd={() => setPreviousIndex(null)}
  //         >
  //           {styleWordInText(messages[previousIndex], wordStyles).map(
  //             (wordElement, index) => (
  //               <React.Fragment key={index}>
  //                 {wordElement}
  //                 {/* Add space if not last word */}
  //                 {index !== messages[previousIndex].split(" ").length - 1 &&
  //                   " "}
  //               </React.Fragment>
  //             )
  //           )}
  //         </div>
  //       )}
  //       <div
  //         className={`${getTextClass(
  //           messages[currentIndex].length
  //         )} slide-text ${isAnimating ? "slide-in" : ""}`}
  //         onAnimationEnd={() => setIsAnimating(false)}
  //       >
  //         {styleWordInText(messages[currentIndex], wordStyles).map(
  //           (wordElement, index) => (
  //             <React.Fragment key={index}>
  //               {wordElement}
  //               {/* Add space if not last word */}
  //               {index !== messages[currentIndex].split(" ").length - 1 && " "}
  //             </React.Fragment>
  //           )
  //         )}
  //       </div>
  //     </div>
  //   );
  // }

  // function countdownRender() {
  //   const countdownNumber = numPrompts - currentIndex - 1;
  //   if (countdownNumber < 100)
  //     return <div className="countdown-l">{countdownNumber}</div>;
  //   else return <div className="countdown-m">{countdownNumber}</div>;
  // }

  function prevButtonRender() {
    if (currentIndex > 0) {
      return (
        <div
          className="button-next-prev button-prev text-color-black shadow-black"
          onClick={prev}
        >
          <img className="button-prev-img" src={arrow} alt="" />
        </div>
      );
    } else {
      return (
        <div
          className="button-next-prev button-prev text-color-black shadow-black box-button-center"
          onClick={prev}
          style={{ visibility: "hidden" }}
        >
          <img className="button-prev-img" src={arrow} alt="" />
        </div>
      );
    }
  }

  async function payment() {
    let response;

    try {
      response = await fetch(props.fetchString + "dopayment", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          roomName: props.roomName,
          mobileNumber: mobileNumber,
          hasStarted: start,
        }),
      });
    } catch (error) {
      console.log(error);
      //TODO: add to language file
      props.showInfoMessage("warning", "Noe gikk galt", false);
    }

    isRunningRef.current = false;

    // Check if response is OK
    if (!response.ok) {
      throw new Error(`HTTP error! Status: ${response.status}`);
    }
    const data = await response.json(); // ✅ Extract JSON from the response
    console.log("Parsed Response:", data);

    if (data.redirectUrl) {
      window.location.href = data.redirectUrl; // ✅ Redirect in the browser
    } else {
      console.error("No redirect URL found.");
    }
  }

  const isRunningRef = useRef(false);

  function startButtonRender() {
    if (start) {
      return <></>;
    }
    if (!gameStarted) {
      return (
        <div
          className="button-primary black-fill button-text-l text-color-pink shadow-pink-opaque box-button-center top-margin-s"
          onClick={handleStartButtonClick}
        >
          {t("start")}
        </div>
      );
    }
    return (
      <div className="top-margin-s" style={{ position: "relative" }}>
        <div style={{ display: "flex", gap: "10px", alignItems: "flex-start" }}>
          <div
            ref={continueButtonRef}
            className="button-primary black-fill button-text-l text-color-pink shadow-pink-opaque box-button-center"
            onClick={handleStartButtonClick}
            style={{ flex: 1, marginTop: 0 }}
          >
            {t("classic.continueGame")}
          </div>
          <div
            className="button-primary black-fill text-color-pink shadow-pink-opaque"
            onClick={toggleStartOver}
            aria-label={t("classic.resumeModal.startOver")}
            aria-expanded={startOverOpen}
            style={{
              marginTop: 0,
              width: continueButtonHeight ? `${continueButtonHeight}px` : undefined,
              height: continueButtonHeight ? `${continueButtonHeight}px` : undefined,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              cursor: "pointer",
              flexShrink: 0,
              boxSizing: "border-box",
            }}
          >
            <svg
              viewBox="0 0 23 23"
              xmlns="http://www.w3.org/2000/svg"
              style={{
                width: "40%",
                height: "auto",
                transform: startOverOpen ? "rotate(0deg)" : "rotate(180deg)",
                transition: "transform 0.25s ease",
              }}
            >
              <path
                d="M21.8203 17.1028L11.6732 4.12842L1.76903 17.1028H21.8203Z"
                fill="currentColor"
                stroke="currentColor"
                strokeWidth="1.17949"
              />
            </svg>
          </div>
        </div>
        <div
          style={{
            position: "absolute",
            top: "100%",
            left: 0,
            right: "-6px",
            marginTop: "10px",
            overflow: "hidden",
            paddingRight: "6px",
            paddingBottom: "8px",
            pointerEvents: startOverOpen ? "auto" : "none",
            zIndex: 20,
          }}
        >
          <div
            style={{
              transform: startOverOpen
                ? "translateY(0)"
                : "translateY(calc(-100% - 10px))",
              transition:
                "transform 0.32s cubic-bezier(0.16, 1, 0.3, 1)",
            }}
          >
            <div
              className="button-primary white-fill button-text-l text-color-black shadow-black box-button-center"
              onClick={handleStartOver}
              style={{ marginTop: 0 }}
            >
              {t("classic.resumeModal.startOver")}
            </div>
          </div>
        </div>
      </div>
    );
  }

  function topBarRender() {
    return (
      <div className="container-row lift-topbar">
        <div className="container-row-start">
          <div
            className="button-tertiary-m button-text-m text-color-black"
            onClick={BackButton}
            style={{ maxHeight: "32.5px" }}
          >
            {t("back")}
          </div>
        </div>
        <div className="text-h3 text-color-black left-margin-s text-force-break">
          {props.roomName}
        </div>
      </div>
    );
  }

  function addEntryRender() {
    if (!start) {
      return (
        <AddEntry
          value={roomInput}
          onChange={setRoomInput}
          onAdd={add}
          textareaRef={textArea}
          t={t}
        />
      );
    } else return <></>;
  }

  function nextButtonRender() {
    return (
      <div
        className="button-next-prev text-color-black shadow-black box-button-center next-button"
        onClick={next}
      >
        {t("next")}
      </div>
    );
  }

  function aiButtonRender() {
    return (
      <div
        className="black-fill button-text-m text-color-pink"
        style={{
          height: "52px",
          cursor: "pointer",
          textAlign: "center",
          lineHeight: "49px",
        }}
        onClick={handleOpenDrawerClick}
      >
        {start ? t("payment.free.cta") : t("classic.addWithAi")}
      </div>
    );
  }

  const handleOpenDrawerClick = () => {
    setIsPaymentDrawerOpen(true);
    setTranslateYDrawer(0); // Reset the translation when opened

    if (!start) {
      setShowPaidPaymentDrawer(true);
    } else {
      fetch(props.fetchString + props.roomName, {
        headers: {
          Accept: "application/json",
        },
      })
        .then((res) => res.json())
        .then((data) => {
          setShowPaidPaymentDrawer(!!data?.data?.hasUsedFreeAI);
        });
    }
  };

  function startRoundRender() {
    return (
      <div
        className="App-header"
        style={showAiButton ? { marginBottom: "52px" } : {}}
      >
        {showAiButton ? (
          <div className="ai-banner">{aiButtonRender()}</div>
        ) : (
          <></>
        )}
        {waitingForPrompts && <WaitingForPromptsRender />}
        <PromptCounter numPrompts={numPrompts - currentIndex - 1} />
        {entryRender(
          divStyle,
          previousIndex,
          currentIndex,
          messages,
          isAnimating,
        )}

        <div className="prev-next-buttons gap-20">
          {prevButtonRender()}
          {nextButtonRender()}
        </div>
      </div>
    );
  }

  const renderMainContent = () => (
    <div
      className="App-header"
      style={showAiButtonBeforeRound ? { marginBottom: "52px" } : {}}
    >
      {showAiButtonBeforeRound && (
        <div className="ai-banner">{aiButtonRender()}</div>
      )}
      {waitingForPrompts && <WaitingForPromptsRender />}
      {!start ? preStartRender() : startedRender()}
    </div>
  );

  function preStartRender() {
    return (
      <>
        <PromptCounter numPrompts={numPrompts} />
        <div>
          {topBarRender()}

          {startButtonRender()}
          <Recommendation
            setOpenRecommendation={setOpenRecommendation}
            openRecommendation={openRecommendation}
            setRoomInput={setRoomInput}
            t={t}
            containerHeight={props.containerHeight}
          />
        </div>
        <div style={{ marginRight: -10 }}>{addEntryRender()}</div>
      </>
    );
  }

  function startedRender() {
    return (
      <>
        {topBarRender()}
        {startRoundRender()}
      </>
    );
  }

  return (
    <div className="App-header">
      {renderMainContent()}

      <PaymentDrawer
        isOpen={isPaymentDrawerOpen}
        setIsOpen={setIsPaymentDrawerOpen}
        translateY={translateYDrawer}
        setTranslateY={setTranslateYDrawer}
        mobileNumber={mobileNumber}
        onChangeMobileInput={onChangeMobileInput}
        payment={payment}
        showInfoMessage={props.showInfoMessage}
        showPaidPaymentDrawer={showPaidPaymentDrawer}
        socket={props.socket}
        roomName={props.roomName}
        start={start}
      />

    </div>
  );
}
